import { Component, ViewChild, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { User, UserProfile, ProductState } from '../../../../models/User';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from '../../../../core/services/breadcrumb/breadcrumb.service';
import { PracticeEvaluationService } from '../../../../services/practice-evaluation.service';
import { AuthStateService } from '../../../../core/states/auth-state.service';
import { UserService } from '../../../../services/user.service';
import { UserContractService } from '../../../../services/user-contract.service';
import { GenericFormComponent } from "../../../../shared/components/generic-crud/generic-form/generic-form.component";
import { GenericForm, GenericFormField } from '../../../../models/GenericCRUD/GenericForm';
import { sanitizeSlug } from '../../../../core/const/functions';
import { NgIf, NgFor, DatePipe, NgClass, CommonModule } from '@angular/common';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { USER_ROLE_LABELS, UserRole } from '../../../../enums/user-role.enum';
import { UserContract } from '../../../../models/UserContract';
import { ToastrService } from 'ngx-toastr';
import { Card } from '../../../../models/Card';
import { GenericCrudService } from '../../../../services/generic-crud.service';
import { FormsModule } from '@angular/forms';
import { CustomDropdownComponent } from '../../../../shared/components/form-controls/custom-dropdown/custom-dropdown.component';
import { RejectReasonModalComponent } from '../../../../shared/components/reject-reason-modal/reject-reason-modal.component';
import { UserWalletComponent } from '../user-wallet/user-wallet.component';
import { StudentCommentsListComponent } from '../../../../shared/components/student-comments/student-comments-list/student-comments-list.component';
import { StudentSkillsProgressComponent, SkillHistoryData } from '../../../../shared/components/practice-evaluation/student-skills-progress.component';
import { PRACTICE_RATING_LABELS, PRACTICE_RATING_EMOJIS, PRACTICE_RATING_COLORS } from '../../../../models/PracticeEvaluation';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { StudentDocumentsTabComponent } from './student-documents-tab/student-documents-tab.component';
import { StudentDocumentService } from '../../../../services/student-document.service';
import { ConfirmationComponent } from '../../../../shared/components/confirmation/confirmation.component';
import { DropdownModule } from 'primeng/dropdown';
import { PaymentMethodSelectorComponent, PaymentData } from '../../../../shared/components/payment-method-selector/payment-method-selector.component';

@Component({
  selector: 'app-user-details',
  standalone: true,
  imports: [GenericFormComponent, NgIf, NgFor, DatePipe, NgClass, PdfViewerModule, CommonModule, FormsModule, UserWalletComponent, StudentCommentsListComponent, StudentSkillsProgressComponent, ModalComponent, StudentDocumentsTabComponent, ConfirmationComponent, DropdownModule, PaymentMethodSelectorComponent, CustomDropdownComponent, RejectReasonModalComponent],
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.css'
})
export class UserDetailsComponent implements AfterViewInit {

  user!: UserProfile;
  activeUserForm: boolean = false;
  activeProductForm: boolean = false;
  userContracts: UserContract[] = [];
  loadingContracts: boolean = false;
  selectedContract: UserContract | null = null;
  contractPdfSrc: string | null = null;
  showContractPdf: boolean = false;

  // Sistema de tabs
  activeTab: 'products' | 'general' | 'cards' | 'wallet' | 'comments' | 'practice-progress' | 'documents' = 'general';
  selectedProductId: number | null = null;
  hasPracticeProgress = false;

  // Sistema de tarjetas DGT
  cards: Card[] = [];
  selectedCards: Set<number> = new Set();
  isLoadingCards: boolean = false;
  isUpdatingStatus: boolean = false;

  // Referencia al componente wallet para acceder a su estado del modal
  @ViewChild(UserWalletComponent) walletComponent!: UserWalletComponent;
  
  // Referencia al componente de comentarios para acceder a su estado del modal
  @ViewChild(StudentCommentsListComponent) commentsListComponent!: StudentCommentsListComponent;
  @ViewChild('documentsTab') documentsTab!: StudentDocumentsTabComponent;

  // Modal de historial de habilidades de práctica
  showSkillHistoryModal = false;
  selectedSkillHistory: SkillHistoryData | null = null;
  ratingLabels = PRACTICE_RATING_LABELS;
  ratingEmojis = PRACTICE_RATING_EMOJIS;
  ratingColors = PRACTICE_RATING_COLORS;

  // Modal de documentos
  showDocumentModal = false;
  documentModalData: any = null;
  
  // Modal de confirmación
  showConfirmationModal = false;
  confirmationQuestion = '';
  confirmationAction: (() => void) | null = null;

  // Custom product form state
  productFormLoading = false;
  productFormSubmitting = false;
  productFormError: string | null = null;
  productsList: any[] = [];
  productFormData: {
    product_id: number | null;
    payment_method: string;
    pos_terminal_id: number | null;
    cash_amount: number | null;
    card_amount: number | null;
    paid_amount: number | null;
  } = { product_id: null, payment_method: 'cash', pos_terminal_id: null, cash_amount: null, card_amount: null, paid_amount: null };
  selectedProductForForm: any = null;
  currentPaymentData: PaymentData = { payment_method: 'cash', pos_terminal_id: null, cash_amount: null, card_amount: null, valid: true };

  // Psicotécnico por producto
  productPsicotecnico: any = null;
  loadingPsicotecnico: boolean = false;
  psicotecnicoFile: File | null = null;
  uploadingPsicotecnico: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private userContractService: UserContractService,
    private genericCrudService: GenericCrudService,
    private studentDocumentService: StudentDocumentService,
    private cdr: ChangeDetectorRef,
    private breadcrumbService: BreadcrumbService,
    private toastr: ToastrService,
    private practiceEvaluationService: PracticeEvaluationService,
    private authState: AuthStateService
  ) {
    const navItem = this.router.getCurrentNavigation()?.extras?.state?.['item'];
    if (navItem) {
      const userName = [navItem.name, navItem.surname].filter(Boolean).join(' ');
      const userId = navItem.id;
      this.breadcrumbService.setBreadcrumb({
        display_name: userName,
        url: `/dashboard/usuarios/${userId}`,
        items: [
          { name: 'Usuarios', url: '/dashboard/usuarios' },
          { name: userName, url: `/dashboard/usuarios/${userId}` }
        ]
      });
    }
    this.loadUser();
  }

  ngAfterViewInit(): void {
    // Forzar detección de cambios después de que la vista se inicialice
    // Esto evita el error ExpressionChangedAfterItHasBeenCheckedError
    this.cdr.detectChanges();
  }

  private loadUser() {
    let userId = this.route.snapshot.paramMap.get('id');
    if (userId) {
      this.userService.get(+userId).subscribe({
        next: response => {
          if (response.data) {
            this.user = response.data;
            const userName = [this.user.name, this.user.surname].filter(Boolean).join(' ');
            this.breadcrumbService.setBreadcrumb({
              display_name: userName || 'Usuario',
              url: `/dashboard/usuarios/${this.user.id}`,
              items: [
                { name: 'Usuarios', url: '/dashboard/usuarios' },
                { name: userName || 'Usuario', url: `/dashboard/usuarios/${this.user.id}` }
              ]
            });
            this.sanitize();
            this.loadUserContracts();

            // Fijar el carnet activo por defecto (select general de la ficha).
            if (!this.selectedProductId && this.productStates.length > 0) {
              this.selectedProductId = this.productStates[0].product_id;
              this.loadProductPsicotecnico();
            }

            // La tab de progreso de práctica solo se muestra si hay contenido.
            this.checkPracticeProgress();
          }
        },
        error: (err) => {
          console.log(err);
        }
      });
    }
  }

  resendContract(contract: UserContract) {
    this.genericCrudService.create(`/contract/send-acceptance/${contract.id}`, {}).subscribe({
      next: (res: any) => res?.success
        ? this.toastr.success(res.message || 'Contrato enviado al alumno')
        : this.toastr.error(res?.message || 'No se pudo enviar el contrato'),
      error: (err: any) => this.toastr.error(err?.error?.message || 'Error al enviar el contrato'),
    });
  }

  private loadUserContracts() {
    if (this.user && this.user.id) {
      this.loadingContracts = true;
      this.userContractService.getUserContracts(this.user.id).subscribe({
        next: response => {
          if (response && response.data && Array.isArray(response.data.contracts)) {
            this.userContracts = response.data.contracts;
          } else {
            this.userContracts = [];
          }
          this.loadingContracts = false;
        },
        error: (err) => {
          console.error('Error loading user contracts:', err);
          this.userContracts = [];
          this.loadingContracts = false;
        }
      });
    }
  }

  viewContractPdf(contract: UserContract) {
    this.selectedContract = contract;

    if (contract.pdf_content) {
      this.contractPdfSrc = `data:application/pdf;base64,${contract.pdf_content}`;
      this.showContractPdf = true;
    } else {
      this.userContractService.getContractPdf(contract.id).subscribe({
        next: (pdfBlob: Blob) => {
          const reader = new FileReader();
          reader.onload = () => {
            this.contractPdfSrc = reader.result as string;
            this.showContractPdf = true;
          };
          reader.readAsDataURL(pdfBlob);
        },
        error: (err) => console.error('Error loading contract PDF:', err)
      });
    }
  }

  closeContractPdf() {
    this.showContractPdf = false;
    this.selectedContract = null;
    this.contractPdfSrc = null;
  }

  get userRole() {
    const roleName = this.user.roles.name;
    const isValidRole = Object.values(UserRole).includes(roleName as UserRole)
    if (isValidRole) {
      return USER_ROLE_LABELS[roleName as UserRole]
    } else {
      return roleName;
    }
  }

  /** El que consulta la ficha es profesor (no admin): vista reducida. */
  get isTeacherViewing(): boolean {
    return this.authState.isTeacher() && !this.authState.hasRole('admin');
  }

  get isStudent(): boolean {
    return this.user?.roles?.name === 'student';
  }

  formFieldsEdit: GenericFormField[] = [
    { name: 'name', type: 'text', label: 'Nombre', validations: 'required' },
    { name: 'surname', type: 'text', label: 'Apellidos', validations: 'nullable' },
    { name: 'email', type: 'email', label: 'Email', validations: 'required|email' },
    { name: 'nif', type: 'text', label: 'NIF/DNI', validations: 'nullable' },
    { name: 'phone', type: 'text', label: 'Teléfono', validations: 'nullable' },
    { name: 'address', type: 'text', label: 'Calle', validations: 'nullable' },
    { name: 'address_number', type: 'text', label: 'Número', validations: 'nullable' },
    { name: 'city', type: 'text', label: 'Localidad', validations: 'nullable' },
    { name: 'province', type: 'text', label: 'Provincia', validations: 'nullable' },
    { name: 'postal_code', type: 'text', label: 'Código Postal', validations: 'nullable' },
    { name: 'password', type: 'password', label: 'Nueva Contraseña', validations: 'nullable' },
    { name: 'rol_id', type: 'select', label: 'Roles', validations: 'required', url: '/role/listAll', singleSelection: true }
  ];

  genericForm: GenericForm = {
    method: 'PUT',
    successTitle: 'Edición correcta',
    errorTitle: 'Error en la edición',
    fields: this.formFieldsEdit,
    name: 'usuario',
    createButtonTitle: '',
    cols: 3,
    createApiSlug: '',
    originalUpdateApiSlug: '/user/update/{{id}}',
    updateApiSlug: '',
    originalFormTitle: 'Editar {{name}}',
    formTitle: '',
    formButtonTitle: 'Editar',
    width: '4xl'
  }

  toggleActiveUserForm() {
    this.activeUserForm = !this.activeUserForm;
  }

  onFinishUserForm() {
    this.toggleActiveUserForm();
    if (this.user) {
      this.userService.get(this.user.id).subscribe({
        next: response => {
          if (response.data) {
            this.user = response.data;
            this.sanitize();
          }
        }
      });
    }
  }


  toggleActiveProductForm() {
    if (!this.activeProductForm && this.user) {
      this.productFormData = { product_id: null, payment_method: 'cash', pos_terminal_id: null, cash_amount: null, card_amount: null, paid_amount: null };
      this.selectedProductForForm = null;
      this.productFormError = null;
      this.activeProductForm = true;
      this.loadProductFormData();
      this.cdr.detectChanges();
    } else {
      this.activeProductForm = false;
    }
  }

  onFinishProductForm() {
    this.toggleActiveProductForm();
    if (this.user) {
      this.userService.get(this.user.id).subscribe({
        next: response => {
          if (response.data) {
            this.user = response.data;
            this.loadUserContracts();
            this.sanitize();
          }
        }
      });
    }
  }

  sanitize() {
    if (this.genericForm.originalFormTitle) {
      this.genericForm.formTitle = sanitizeSlug(this.genericForm.originalFormTitle, this.user);
    }

    if (this.genericForm.originalUpdateApiSlug) {
      this.genericForm.updateApiSlug = sanitizeSlug(this.genericForm.originalUpdateApiSlug, this.user);
    }

    if (this.genericForm.originalUploadFilesUrl) {
      this.genericForm.uploadFilesUrl = sanitizeSlug(this.genericForm.originalUploadFilesUrl, this.user);
    }
  }

  loadProductFormData() {
    this.productFormLoading = true;
    this.genericCrudService.getSelectOptions('/product/listAll').subscribe({
      next: res => {
        this.productsList = res.data || [];
        this.productFormLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.productsList = [];
        this.productFormLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onProductSelected() {
    const id = this.productFormData.product_id;
    this.selectedProductForForm = id ? (this.productsList.find(p => p.id == id) || null) : null;
    this.productFormData.cash_amount = null;
    this.productFormData.card_amount = null;
    // Por defecto, el importe pagado ahora es el precio total del producto.
    this.productFormData.paid_amount = this.selectedProductForForm?.total_price ?? null;
  }

  onPaymentDataChange(data: PaymentData) {
    this.currentPaymentData = data;
    this.productFormData.payment_method = data.payment_method;
    this.productFormData.pos_terminal_id = data.pos_terminal_id;
    this.productFormData.cash_amount = data.cash_amount;
    this.productFormData.card_amount = data.card_amount;
  }

  isProductFormValid(): boolean {
    if (!this.productFormData.product_id) return false;
    return this.currentPaymentData.valid;
  }

  submitProductForm() {
    if (!this.isProductFormValid() || !this.user) return;
    this.productFormSubmitting = true;
    this.productFormError = null;

    const payload: any = {
      product_id: this.productFormData.product_id,
      payment_method: this.productFormData.payment_method,
    };
    if (this.productFormData.payment_method === 'card' || this.productFormData.payment_method === 'split') {
      payload.pos_terminal_id = this.productFormData.pos_terminal_id;
    }
    if (this.productFormData.payment_method === 'split') {
      payload.cash_amount = this.productFormData.cash_amount;
      payload.card_amount = this.productFormData.card_amount;
    } else {
      // Pago simple: importe que paga el alumno ahora (define pre/matriculado).
      payload.paid_amount = this.productFormData.paid_amount;
    }

    this.genericCrudService.create(`/user/assign-product/${this.user.id}`, payload).subscribe({
      next: res => {
        this.productFormSubmitting = false;
        if (res.success) {
          this.onFinishProductForm();
        } else {
          this.productFormError = res.message || 'Error al contratar el producto';
          this.cdr.detectChanges();
        }
      },
      error: err => {
        this.productFormSubmitting = false;
        this.productFormError = err?.error?.message || 'Error al contratar el producto';
        this.cdr.detectChanges();
      }
    });
  }

  setActiveTab(tab: 'products' | 'general' | 'cards' | 'wallet' | 'comments' | 'practice-progress' | 'documents') {
    this.activeTab = tab;
    if (tab === 'cards' && this.cards.length === 0) {
      this.loadCards();
    }
    // El carnet activo es general (select de la ficha): no se reinicia por tab.
    if (!this.selectedProductId && this.productStates.length > 0) {
      this.selectedProductId = this.productStates[0].product_id;
      this.loadProductPsicotecnico();
    }
  }

  selectProduct(productId: number) {
    this.selectedProductId = productId;
    this.loadProductPsicotecnico();
  }

  matriculating = false;
  showPayModal = false;
  payAmount: number | null = null;
  payPaymentData: any = { payment_method: 'cash', pos_terminal_id: null, cash_amount: null, card_amount: null, valid: true };

  /** ¿El carnet seleccionado está prematriculado (pendiente de pagar/matricular)? */
  get canMatricular(): boolean {
    return this.selectedProductState?.state?.name === 'PREMATRICULADO';
  }

  openPayModal(): void {
    this.payAmount = null;
    this.payPaymentData = { payment_method: 'cash', pos_terminal_id: null, cash_amount: null, card_amount: null, valid: true };
    this.showPayModal = true;
  }

  onPayPaymentDataChange(data: any): void {
    this.payPaymentData = data;
  }

  /** Pagar producto: recarga el monedero con lo pagado y matricula (cobra la matrícula). */
  confirmPay(): void {
    if (!this.user?.id || !this.selectedProductId || this.matriculating) return;
    const payload: any = {
      paid_amount: this.payAmount ?? 0,
      payment_method: this.payPaymentData?.payment_method ?? 'cash',
    };
    if (this.payPaymentData?.pos_terminal_id) {
      payload.pos_terminal_id = this.payPaymentData.pos_terminal_id;
    }
    if (payload.payment_method === 'split') {
      payload.cash_amount = this.payPaymentData?.cash_amount ?? null;
      payload.card_amount = this.payPaymentData?.card_amount ?? null;
    }

    this.matriculating = true;
    this.genericCrudService.create(`/user/matricular/${this.user.id}/${this.selectedProductId}`, payload).subscribe({
      next: (res: any) => {
        this.matriculating = false;
        this.showPayModal = false;
        this.toastr.success(res?.message || 'Alumno matriculado correctamente.');
        this.loadUser();
      },
      error: (e) => {
        this.matriculating = false;
        this.toastr.error(e?.error?.message || 'No se pudo matricular (revisa el importe y el saldo).');
      },
    });
  }

  /** La tab de progreso de práctica solo se muestra si el alumno tiene progreso. */
  private checkPracticeProgress(): void {
    if (!this.user?.id) return;
    this.practiceEvaluationService.getStudentProgress(this.user.id).subscribe({
      next: (res: any) => {
        this.hasPracticeProgress = (res?.data?.progress_by_license ?? []).length > 0;
      },
      error: () => (this.hasPracticeProgress = false),
    });
  }

  /** Opciones del selector de carnet (un carnet por producto contratado). */
  get productDropdownOptions(): { id: number; name: string }[] {
    return this.productStates
      .filter(ps => ps.product_id)
      .map(ps => ({ id: ps.product_id as number, name: ps.product?.name ?? 'Producto' }));
  }

  loadProductPsicotecnico(): void {
    if (!this.user?.id || !this.selectedProductId) {
      this.productPsicotecnico = null;
      return;
    }
    this.loadingPsicotecnico = true;
    this.studentDocumentService.getDocuments(this.user.id, 1, undefined, { product_id: this.selectedProductId }).subscribe({
      next: (res) => {
        const docs = res.data?.data || [];
        this.productPsicotecnico = docs.find((d: any) => d.document_type === 'CERTIFICADO_MEDICO') || null;
        this.loadingPsicotecnico = false;
      },
      error: () => {
        this.productPsicotecnico = null;
        this.loadingPsicotecnico = false;
      }
    });
  }

  onPsicotecnicoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.psicotecnicoFile = input.files[0];
      this.uploadPsicotecnico();
    }
  }

  private uploadPsicotecnico(): void {
    if (!this.psicotecnicoFile || !this.user?.id || !this.selectedProductId) return;
    this.uploadingPsicotecnico = true;
    const formData = new FormData();
    formData.append('user_id', String(this.user.id));
    formData.append('product_id', String(this.selectedProductId));
    formData.append('document_type', 'CERTIFICADO_MEDICO');
    formData.append('file', this.psicotecnicoFile);
    this.studentDocumentService.uploadDocument(formData).subscribe({
      next: () => {
        this.psicotecnicoFile = null;
        this.uploadingPsicotecnico = false;
        this.loadProductPsicotecnico();
      },
      error: () => {
        this.uploadingPsicotecnico = false;
      }
    });
  }

  acceptPsicotecnico(): void {
    if (!this.productPsicotecnico) return;
    this.studentDocumentService.verifyDocument(this.productPsicotecnico.id).subscribe({
      next: () => {
        this.toastr.success('Psicotécnico aceptado');
        this.loadProductPsicotecnico();
      },
      error: (e) => this.toastr.error(e.error?.message || 'Error al aceptar el psicotécnico'),
    });
  }

  showRejectPsicoModal = false;

  rejectPsicotecnico(): void {
    if (!this.productPsicotecnico) return;
    this.showRejectPsicoModal = true;
  }

  confirmRejectPsicotecnico(feedback: string): void {
    this.showRejectPsicoModal = false;
    if (!this.productPsicotecnico) return;
    this.studentDocumentService.rejectDocument(this.productPsicotecnico.id, feedback).subscribe({
      next: () => {
        this.toastr.success('Psicotécnico rechazado');
        this.loadProductPsicotecnico();
      },
      error: (e) => this.toastr.error(e.error?.message || 'Error al rechazar el psicotécnico'),
    });
  }

  confirmDeletePsicotecnico(): void {
    if (!this.productPsicotecnico) return;
    this.showConfirmationModal = true;
    this.confirmationQuestion = '¿Estas seguro de eliminar el psicotecnico?';
    this.confirmationAction = () => {
      this.studentDocumentService.deleteDocument(this.productPsicotecnico.id).subscribe({
        next: () => {
          this.productPsicotecnico = null;
          this.psicotecnicoFile = null;
        }
      });
    };
  }

  downloadPsicotecnico(): void {
    if (!this.productPsicotecnico) return;
    this.studentDocumentService.downloadDocument(this.productPsicotecnico.id).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const media = this.productPsicotecnico.media?.[0];
        a.download = media?.file_name || 'psicotecnico.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    });
  }

  // Filtrar solo estados con producto (excluir estados sin producto/legacy)
  // IMPORTANTE: Usar current_state_records que tiene los datos completos (transitions, proof_definitions)
  get productStates(): ProductState[] {
    // Estados por producto: preferimos los registros completos.
    if (this.user?.current_state_records && this.user.current_state_records.length > 0) {
      return this.user.current_state_records
        .filter(ps => ps.product_id !== null && ps.product !== null)
        .map(record => ({
          product_id: record.product_id,
          product: record.product,
          state: record.state,
          transitioned_at: record.transitioned_at
        }));
    }

    // Fallback a current_states si no hay registros.
    if (this.user?.current_states && this.user.current_states.length > 0) {
      return this.user.current_states.filter(ps => ps.product_id !== null && ps.product !== null);
    }

    return [];
  }

  get selectedProductState(): ProductState | undefined {
    if (!this.productStates || !this.selectedProductId) return undefined;
    return this.productStates.find(ps => ps.product_id === this.selectedProductId);
  }

  // Obtener cursos del producto seleccionado
  getProductCourses() {
    if (!this.user?.course_calls || !this.selectedProductId) {
      console.log('No course_calls or selectedProductId:', {
        hasCalls: !!this.user?.course_calls,
        selectedProductId: this.selectedProductId
      });
      return [];
    }

    return this.user.course_calls.filter((cc: any) => {
      return cc.course?.products?.some((p: any) => p.id === this.selectedProductId);
    });
  }

  // Obtener exámenes del producto seleccionado
  getProductExams() {
    if (!this.user?.proofs || !this.selectedProductId) {
      console.log('No proofs or selectedProductId:', {
        hasProofs: !!this.user?.proofs,
        selectedProductId: this.selectedProductId
      });
      return [];
    }

    return this.user.proofs.filter((proof: any) => {
      return proof.proofDefinition?.products?.some((p: any) => p.id === this.selectedProductId);
    });
  }

  // Obtener tarjetas del producto seleccionado
  /** Contratos del carnet seleccionado. */
  getProductContracts(): UserContract[] {
    if (!this.selectedProductId) return this.userContracts;
    return this.userContracts.filter(c => c.product?.id === this.selectedProductId);
  }

  /** Licencias del carnet seleccionado (para filtrar tarjetas, que van por licencia). */
  get selectedProductLicenseIds(): number[] {
    const product: any = this.selectedProductState?.product;
    const defs: any[] = product?.proof_definitions ?? product?.proofDefinitions ?? [];
    const ids = new Set<number>();
    defs.forEach(d => (d?.licenses ?? []).forEach((l: any) => l?.id && ids.add(l.id)));
    return Array.from(ids);
  }

  getProductCards() {
    if (!this.cards || !this.selectedProductId) return [];
    const licenseIds = this.selectedProductLicenseIds;
    // Las tarjetas van por licencia; si conocemos las del carnet, filtramos.
    return licenseIds.length ? this.cards.filter(c => licenseIds.includes(c.license_id)) : this.cards;
  }

  // Obtener pagos/movimientos del producto seleccionado
  getProductPayments() {
    // Por ahora retornamos array vacío hasta que tengamos la estructura de wallet_movements
    return [];
  }

  loadCards() {
    if (!this.user?.id) return;

    this.isLoadingCards = true;
    this.genericCrudService.list('/cards?user_id=' + this.user.id, []).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.cards = Array.isArray(response.data) ? response.data : response.data.data || [];
        }
        this.isLoadingCards = false;
      },
      error: (error) => {
        console.error('Error loading cards:', error);
        this.isLoadingCards = false;
      }
    });
  }

  toggleCardSelection(cardId: number) {
    if (this.selectedCards.has(cardId)) {
      this.selectedCards.delete(cardId);
    } else {
      this.selectedCards.add(cardId);
    }
  }

  toggleSelectAll() {
    if (this.selectedCards.size === this.cards.length) {
      this.selectedCards.clear();
    } else {
      this.cards.forEach(card => this.selectedCards.add(card.id));
    }
  }

  get allSelected(): boolean {
    return this.cards.length > 0 && this.selectedCards.size === this.cards.length;
  }

  bulkChangeState(state: string) {
    if (this.selectedCards.size === 0) return;

    this.isUpdatingStatus = true;
    const cardIds = Array.from(this.selectedCards);

    this.genericCrudService.reqByMethod('/cards/bulk-change-state', 'POST', {
      card_ids: cardIds,
      state: state,
      reason: `Cambio masivo a estado ${state}`
    }).subscribe({
      next: (response) => {
        if (response.success) {
          this.loadCards();
          this.selectedCards.clear();
        }
        this.isUpdatingStatus = false;
      },
      error: (error) => {
        console.error('Error changing card states:', error);
        this.isUpdatingStatus = false;
      }
    });
  }

  bulkRequestCards() {
    this.bulkChangeState('SOLICITADA');
  }

  bulkMarkAsReceived() {
    this.bulkChangeState('RECIBIDA EN AUTOESCUELA');
  }

  bulkMarkAsDelivered() {
    this.bulkChangeState('ENTREGADA');
  }

  // Getters para evitar ExpressionChangedAfterItHasBeenCheckedError
  get showWalletModal(): boolean {
    return !!(this.walletComponent?.showGenericDialog && this.walletComponent?.currentGenericForm);
  }

  get showCommentsModal(): boolean {
    return !!(this.commentsListComponent?.showGenericDialog && this.commentsListComponent?.currentGenericForm);
  }

  // Métodos para manejar el modal del wallet
  closeWalletModal() {
    if (this.walletComponent) {
      this.walletComponent.showGenericDialog = false;
    }
  }

  onWalletFormSuccess() {
    this.closeWalletModal();
    // Recargar datos del componente hijo
    if (this.walletComponent) {
      this.walletComponent.handleFormSuccess(null);
    }
  }

  // Wallet recharge custom modal
  get showWalletRechargeModal(): boolean {
    return !!this.walletComponent?.showRechargeForm;
  }

  closeWalletRechargeModal() {
    if (this.walletComponent) {
      this.walletComponent.closeRechargeForm();
    }
  }

  // Métodos para manejar el modal de comentarios
  closeCommentsModal() {
    if (this.commentsListComponent) {
      this.commentsListComponent.closeModal();
    }
  }

  onCommentsFormSuccess() {
    this.closeCommentsModal();
    // Recargar datos del componente hijo
    if (this.commentsListComponent) {
      this.commentsListComponent.handleFormSuccess(null);
    }
  }

  // Métodos para manejar el modal de historial de habilidades
  openSkillHistoryModal(data: SkillHistoryData) {
    this.selectedSkillHistory = data;
    this.showSkillHistoryModal = true;
  }

  closeSkillHistoryModal() {
    this.showSkillHistoryModal = false;
    this.selectedSkillHistory = null;
  }

  formatHistoryDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getRatingLabel(rating: string): string {
    return this.ratingLabels[rating as keyof typeof this.ratingLabels] || rating;
  }

  getRatingEmoji(rating: string): string {
    return this.ratingEmojis[rating as keyof typeof this.ratingEmojis] || '';
  }

  getRatingColor(rating: string): string {
    return this.ratingColors[rating as keyof typeof this.ratingColors] || '';
  }

  onUserDocumentsUpdated(): void {
    this.loadUser();
  }

  onOpenDocumentModal(data: {isEditing: boolean, document?: any, formData?: any}): void {
    this.showDocumentModal = true;
    this.documentModalData = data;
  }

  onCloseDocumentModal(): void {
    this.showDocumentModal = false;
    this.documentModalData = null;
  }

  onOpenConfirmation(data: {question: string, action: () => void}): void {
    this.showConfirmationModal = true;
    this.confirmationQuestion = data.question;
    this.confirmationAction = data.action;
  }

  handleConfirmAction(): void {
    if (this.confirmationAction) {
      this.confirmationAction();
    }
    this.showConfirmationModal = false;
    this.confirmationAction = null;
  }

  handleCancelAction(): void {
    this.showConfirmationModal = false;
    this.confirmationAction = null;
  }

}
